import axios from "axios";
import { beginBusy } from "./submitGuard";

export const getToken = () => {
  let token = sessionStorage.getItem("token");
  return token;
};

// ---- double-submit guard -------------------------------------------------
// The same POST / PUT / PATCH / DELETE (same url + same body) sent again while the first is still
// running is NOT sent to the server again: it gets the first request's answer.
const inFlight = new Map();
const bodyKey = (data) => {
  try {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      const parts = [];
      data.forEach((v, k) => parts.push(`${k}=${v && typeof v === "object" ? `${v.name}:${v.size}` : v}`));
      return parts.join("&");
    }
    return typeof data === "string" ? data : JSON.stringify(data ?? "");
  } catch (e) {
    return String(Math.random()); // unknown body -> never treat as duplicate
  }
};
const requestKey = (config) => {
  const method = String(config.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return null;
  if (config.allowDuplicate) return null;
  return `${method} ${config.url} ${JSON.stringify(config.params || {})} ${bodyKey(config.data)}`;
};
const attachDuplicateGuard = (instance) => {
  instance.interceptors.request.use((config) => {
    const key = requestKey(config);
    if (!key) return config;
    const first = inFlight.get(key);
    if (first) {
      // answer with the running request's result, nothing goes to the server
      config.adapter = () => first.promise.then((res) => ({ ...res, config }));
      config.__dupOf = key;
      return config;
    }
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    promise.catch(() => {});
    // safety: never keep a key longer than 30 s (a lost answer must not block the next save)
    const timer = setTimeout(() => {
      if (inFlight.get(key)?.promise === promise) inFlight.delete(key);
    }, 30000);
    inFlight.set(key, { promise, resolve, reject, timer });
    config.__guardKey = key;
    config.__endBusy = beginBusy(); // spinner on the clicked Save button
    return config;
  });
  const settle = (config, ok, value) => {
    if (config?.__endBusy) config.__endBusy();
    const key = config?.__guardKey;
    if (!key) return;
    const entry = inFlight.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    inFlight.delete(key);
    (ok ? entry.resolve : entry.reject)(value);
  };
  instance.interceptors.response.use(
    (response) => { settle(response.config, true, response); return response; },
    (error) => { settle(error?.config, false, error); return Promise.reject(error); }
  );
  return instance;
};

const attachAuthInterceptor = (instance) => {
  attachDuplicateGuard(instance);
  instance.interceptors.response.use(
    (response) => {
      try {
        const refreshedToken =
          response.headers?.["x-refreshed-token"] ||
          response.headers?.["x-access-token"] ||
          response.headers?.["authorization"];
        if (refreshedToken) {
          sessionStorage.setItem("token", refreshedToken);
        }

        const refreshedPermToken =
          response.headers?.["x-refreshed-permissiontoken"] ||
          response.headers?.["permissiontoken"];
        if (refreshedPermToken) {
          sessionStorage.setItem("permissiontoken", refreshedPermToken);
        }
      } catch (e) {
        // ignore storage errors in environments without sessionStorage
      }
      return response;
    },
    (error) => {
      if (error?.response) {
        const status = error.response.status;
        const errMsg = error.response.data?.errors || error.response.data?.message;
        if (
          status === 401 &&
          (errMsg === "This user has been deactivated by the Admin. Please contact your Admin." ||
            errMsg === "Please authenticate" ||
            errMsg === "Invalid User" ||
            errMsg === "Session expired. Please log in again.")
        ) {
          // public share pages (/view/<token>) work without login – never send them to the login page
          if (/^\/view\//.test(window.location.pathname)) return Promise.reject(error);
          sessionStorage.clear();
          localStorage.clear();
          if (errMsg === "This user has been deactivated by the Admin. Please contact your Admin.") {
            alert(errMsg);
          }
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    }
  );
  return instance;
};

export const getAxios = () => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    Authorization: `${token}`,
  };
  const instance = axios.create({ headers });
  return attachAuthInterceptor(instance);
};

export const getAxiosMultipart = () => {
  const token = getToken();
  const headers = { Authorization: `${token}` };
  const instance = axios.create({ headers });
  return attachAuthInterceptor(instance);
};
export function convertTo12HourFormat(time24Hour) {
  const [hours, minutes] = time24Hour.split(":").map(Number); // Parse hours and minutes as integers
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes < 10 ? "0" : ""}${minutes} ${period}`;
}
